/**
 * Customer detail: the customer's customer-portal logins (tracker item 45, phase 4). ADMIN and
 * OFFICE_MANAGER. Lists the logins (waiting for the customer to accept, active, disabled) with how each was
 * linked, and offers Invite to portal (an emailed set-password link, 7 days, single use), Resend, Disable
 * (the login can no longer sign in) and Unlink (the login stays but sees nothing). Disable and Unlink ask
 * first. The portal can be turned off in Settings > Customer portal: invitations are then refused (422).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, ICompanySettingsApi, IRequestsApi } from '../api';
import * as types from '../api/types';
import { getErrorCode, getErrorMessage } from '../utils/apiError';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from './formStyles';
import { Toast } from './Toast';

type Api = IRequestsApi & ICompanySettingsApi;

const LINKED_VIA: Record<types.CustomerPortalLogin['linkedVia'], string> = {
  INVITE: 'Invited by the office',
  INVOICE_CODE: 'Registered with an invoice code',
  AUTO_EMAIL: 'Matched by email',
  STAFF: 'Linked by the office',
};

export function loginState(login: types.CustomerPortalLogin): { label: string; className: string } {
  if (!login.isActive) return { label: 'Disabled', className: 'bg-gray-100 text-gray-700' };
  if (login.status === 'PENDING_EMAIL') return { label: 'Waiting for the customer', className: 'bg-yellow-100 text-yellow-800' };
  return { label: 'Active', className: 'bg-green-100 text-green-800' };
}

/** The message of a failed invite / resend / disable / unlink, with the settings hint where it helps. */
function portalError(error: unknown, fallback: string): React.ReactNode {
  const code = getErrorCode(error);
  const message = getErrorMessage(error, fallback);
  if (code === 'PORTAL_DISABLED') return <>The customer portal is turned off. Turn it on in <Link to="/settings/customer-portal" className="underline">Settings &gt; Customer portal</Link>.</>;
  if (code === 'PORTAL_LOGIN_LIMIT') return <>{message} Raise the limit in <Link to="/settings/customer-portal" className="underline">Settings &gt; Customer portal</Link>.</>;
  return message;
}

export function CustomerPortalPanel({ customer }: { customer: Pick<types.Customer, 'id' | 'name' | 'email'> }) {
  const api = useMemo<Api>(() => createAPIClient(), []);
  const [logins, setLogins] = useState<types.CustomerPortalLogin[] | null>(null);
  const [portalOn, setPortalOn] = useState<boolean | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [email, setEmail] = useState(customer.email ?? '');
  const [name, setName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [busyUser, setBusyUser] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ userId: string; action: 'disable' | 'unlink' } | null>(null);
  const [error, setError] = useState<React.ReactNode>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setLogins(await api.listCustomerPortalLogins(customer.id));
    } catch (cause) {
      setLoadError(getErrorMessage(cause, 'Could not load the portal logins'));
    }
    try {
      setPortalOn((await api.getCompanySettings()).portalEnabled !== false);
    } catch {
      setPortalOn(null);
    }
  }, [api, customer.id]);

  useEffect(() => { void load(); }, [load]);

  const emailProblem = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? 'Enter the email address to invite' : null;

  async function invite(event: React.FormEvent) {
    event.preventDefault();
    if (emailProblem) { setError(emailProblem); return; }
    setInviting(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.inviteCustomerPortalUser(customer.id, { email: email.trim(), ...(name.trim() ? { name: name.trim() } : {}) });
      setNotice(
        result.delivery === 'QUEUED'
          ? `Invitation sent to ${result.email}. The link works once and expires in 7 days.`
          : `The invitation for ${result.email} was created but could not be emailed${result.skipReason ? ` (${result.skipReason})` : ''}. Check Settings > Notifications, then use Resend.`
      );
      setName('');
      await load();
    } catch (cause) {
      setError(portalError(cause, 'Could not send the invitation'));
    } finally {
      setInviting(false);
    }
  }

  async function act(userId: string, work: () => Promise<unknown>, success: string, failure: string) {
    setBusyUser(userId);
    setError(null);
    setNotice(null);
    try {
      const result = await work();
      if (Array.isArray(result)) setLogins(result as types.CustomerPortalLogin[]);
      setConfirm(null);
      setNotice(success);
    } catch (cause) {
      setConfirm(null);
      setError(portalError(cause, failure));
      await load();
    } finally {
      setBusyUser(null);
    }
  }

  return (
    <section aria-labelledby="customer-portal-heading" className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6" data-testid="customer-portal-panel">
      <h2 id="customer-portal-heading" className="text-lg font-semibold text-gray-900">Customer portal</h2>
      <p className="mt-1 text-sm text-gray-600">
        Logins that let {customer.name} sign in to see their own invoices and jobs, pay online and ask for service. Each login belongs to exactly one customer.
      </p>
      {portalOn === false && (
        <p role="note" className="mt-3 rounded bg-amber-50 p-3 text-sm text-amber-900">
          The customer portal is turned off for your company: nobody can sign in and invitations are refused. Turn it on in <Link to="/settings/customer-portal" className="underline">Settings &gt; Customer portal</Link>.
        </p>
      )}
      {notice && <Toast kind="success">{notice}</Toast>}
      {error && <p role="alert" className="mt-3 rounded bg-red-50 p-3 text-sm text-red-800">{error}</p>}
      {loadError && <p role="alert" className="mt-3 rounded bg-red-50 p-3 text-sm text-red-800">{loadError} <button type="button" className="underline" onClick={() => void load()}>Try again</button></p>}

      {logins === null && !loadError && <p role="status" className="mt-3 text-sm text-gray-600">Loading portal logins…</p>}
      {logins !== null && logins.length === 0 && <p className="mt-3 text-sm text-gray-600" data-testid="no-logins">No portal logins yet.</p>}
      {logins !== null && logins.length > 0 && (
        <ul className="mt-3 divide-y divide-gray-100" aria-label="Portal logins">
          {logins.map((login) => {
            const state = loginState(login);
            const busy = busyUser === login.userId;
            const confirming = confirm?.userId === login.userId ? confirm.action : null;
            return (
              <li key={login.userId} className="py-3" data-testid="portal-login">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="break-all font-medium text-gray-900">{login.email}</p>
                    <p className="text-xs text-gray-500">{login.name ? `${login.name} · ` : ''}{LINKED_VIA[login.linkedVia]}</p>
                  </div>
                  <span className={`rounded px-2 py-0.5 text-xs font-semibold ${state.className}`}>{state.label}</span>
                </div>
                {confirming ? (
                  <div className="mt-2 rounded-md bg-amber-50 p-3 ring-1 ring-amber-200" role="group" aria-label={`Confirm ${confirming}`}>
                    <p className="text-sm text-amber-900">
                      {confirming === 'disable'
                        ? `Disable ${login.email}? They are signed out at once and can no longer sign in. Their history stays.`
                        : `Unlink ${login.email} from ${customer.name}? The login stays but sees no customer data until it is linked again.`}
                    </p>
                    <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                      <button
                        type="button" className={primaryButtonClass} disabled={busy}
                        onClick={() => void (confirming === 'disable'
                          ? act(login.userId, () => api.disableCustomerPortalUser(customer.id, login.userId), `${login.email} was disabled.`, 'Could not disable the login')
                          : act(login.userId, () => api.unlinkCustomerPortalUser(customer.id, login.userId), `${login.email} was unlinked.`, 'Could not unlink the login'))}
                      >
                        {confirming === 'disable' ? 'Yes, disable' : 'Yes, unlink'}
                      </button>
                      <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => setConfirm(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {login.isActive && login.status === 'PENDING_EMAIL' && (
                      <button
                        type="button" className={secondaryButtonClass} disabled={busy}
                        onClick={() => void act(login.userId, () => api.resendCustomerPortalInvite(customer.id, login.userId), `A new invitation was sent to ${login.email}.`, 'Could not resend the invitation')}
                      >
                        Resend invitation
                      </button>
                    )}
                    {login.isActive && (
                      <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => setConfirm({ userId: login.userId, action: 'disable' })}>Disable</button>
                    )}
                    <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => setConfirm({ userId: login.userId, action: 'unlink' })}>Unlink</button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <form onSubmit={(e) => void invite(e)} className="mt-4 space-y-3 border-t border-gray-100 pt-4" aria-label="Invite to portal" noValidate>
        <h3 className="text-sm font-semibold text-gray-800">Invite to portal</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="portal-invite-email" className={labelClass}>Email address</label>
            <input id="portal-invite-email" type="email" className={`${inputClass} min-h-[44px]`} value={email} disabled={inviting} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
          </div>
          <div>
            <label htmlFor="portal-invite-name" className={labelClass}>Name (optional)</label>
            <input id="portal-invite-name" className={`${inputClass} min-h-[44px]`} value={name} disabled={inviting} placeholder={customer.name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
          </div>
        </div>
        <button type="submit" className={primaryButtonClass} disabled={inviting}>{inviting ? 'Sending…' : 'Invite to portal'}</button>
      </form>
    </section>
  );
}
